import React, { useState } from 'react';
import { Dialog } from '@material-ui/core';
import { batch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';

import BookingConfirmation from 'components/BookingForm/BookingConfirmation/BookingConfirmation';
import BookingSummary from 'components/BookingForm/BookingSummary/BookingSummary';
import { Header, Layout } from 'components/Layout';
import { NavigationBar } from 'components/NavigationBar';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import {
  clearFamilyBookingReservedSlots,
  FAMILY_BOOKING_NOTE_MAX_LENGTH,
  setFamilyBookingHomeAddress,
  setFamilyBookingNote,
  setFamilyBookingPostalCode,
  setReviewOpen,
} from 'redux/familyBookingSlice';
import { createFamilyBookingAPI } from 'services/APIs';
import { loadOtpToken } from 'utils/storage';
import useIsMobile from 'hooks/useIsMobile';
import { getIsPatientNoteRequired } from 'utils/getIsPatientNoteRequired';

import {
  MYSELF_AND_OTHERS_OPTION,
  ModeOption,
  OTHERS_OPTION,
} from '../../utils/constants';
import BookingPatients from './BookingPatients/BookingPatients';
import AdditionalInformation from './AdditionalInformation/AdditionalInformation';
import InsuranceConfirmation from './InsuranceConfirmation/InsuranceConfirmation';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';

import NavigationController from '../NavigationController/NavigationController';
import { buildFamilyBookingPayload } from '../utils/buildFamilyBookingPayload';
import {
  expireFamilyBookingSession,
  isFamilyBookingSessionExpired,
} from '../utils/familyBookingSession';
import { hasHomeAddressOnFile, resolvePatientBookability } from '../utils/utils';

import styles from './ReviewAppointment.module.scss';
import { renderToast } from 'components/CustomToast/CustomToast';

interface ReviewAppointmentProps {
  mode: ModeOption;
}

const renderSchedulingFor = (count: number) => (
  <>
    Scheduling for <b>{count}</b> {count === 1 ? 'person' : 'people'}
  </>
);

const ReviewAppointment = ({ mode }: ReviewAppointmentProps) => {

  const { search } = useLocation();

  const dispatch = useAppDispatch();

  const [isInsuranceDone, setIsInsuranceDone] = useState(false);

  const [createdBookingIds, setCreatedBookingIds] = useState<string[] | null>(
    null
  );

  const [isLoading, setIsLoading] = useState(false);

  const {
    familyBookingSlice: {
      contactUser,
      selectedPatients,
      note,
      homeAddress,
      postalCode,
      step1: { phoneNumber: phoneNumberByStep1 },
      step2: { contactPatient },
    },
    clinicDetailsSlice: { clinicData },
    authSlice: { isLoggedIn, tempAccessToken },
  } = useAppSelector((state) => state);

  const isMobile = useIsMobile();

  const isLoadedInsideIframe = useIsLoadedInsideIframe();

  const handleOnBackClick = () => {
    batch(() => {
      dispatch(setReviewOpen(false));

      dispatch(setFamilyBookingNote(''));

      dispatch(setFamilyBookingHomeAddress(''));

      dispatch(setFamilyBookingPostalCode(''));
    });
  };

  const handleOnSubmit = async () => {
    try {
      setIsLoading(true);

      const payload = buildFamilyBookingPayload({
        clinicId: clinicData.id,
        contactUser,
        bookablePatients,
        note,
        homeAddress,
        postalCode,
        isLoggedIn: Boolean(isLoggedIn),
        search,
        insurance: contactPatient.insurance,
        isPayForMyself: contactPatient.isPayForMyself,
        insurancePaymentMethod: contactPatient.insurancePaymentMethod,
      });

      const response = await createFamilyBookingAPI(payload, {
        otpToken: loadOtpToken(phoneNumberByStep1),
        tmpAccessToken: tempAccessToken,
      });

      setCreatedBookingIds(response.bookings.map((item) => item.bookingId));

      dispatch(clearFamilyBookingReservedSlots());
    } catch (error: any) {
      if (isFamilyBookingSessionExpired(error, phoneNumberByStep1)) {
        expireFamilyBookingSession({
          dispatch,
          phoneNumber: phoneNumberByStep1,
        });

        return;
      }

      let message = 'Something went wrong. Please try again later';

      if (error?.response?.status === 400) {
        message = error.response.data.message;
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }

      renderToast({ message });
    } finally {
      setIsLoading(false);
    }
  };

  const bookingPatients = selectedPatients.map(resolvePatientBookability);

  const bookablePatients = bookingPatients.filter((item) => item.isBookable);

  const isInsurancePageDisplayed =
    clinicData.isInsuranceEnabled &&
    !contactPatient.isPayForMyself &&
    !!contactPatient.insurance &&
    !!createdBookingIds?.length;

  const isMyselfAndOthersMode = mode.id === MYSELF_AND_OTHERS_OPTION.id;

  const isOthersMode = mode.id === OTHERS_OPTION.id;

  const isSchedulingCountInSummary = !isMobile || isMyselfAndOthersMode;

  const isSchedulingCountUnderPatients = isMobile && isOthersMode;

  const isInsuranceStepPending = isInsurancePageDisplayed && !isInsuranceDone;

  const hasNoBookablePatient = bookablePatients.length === 0;

  const isNoteTooLong = note.length > FAMILY_BOOKING_NOTE_MAX_LENGTH;

  const isNoteRequired = bookablePatients.some((item) =>
    getIsPatientNoteRequired(
      clinicData.services,
      item.bookingServiceId || item.serviceId
    )
  );

  const isNoteMissing = isNoteRequired && !note.trim();

  const hasBookablePatientWithoutHomeAddress = bookablePatients.some(
    (item) => !hasHomeAddressOnFile(item)
  );

  const isHomeAddressDisplayed =
    clinicData.isHomeAddressEnabled && hasBookablePatientWithoutHomeAddress;

  const isHomeAddressIncomplete = !homeAddress.trim() || !postalCode.trim();

  const isHomeAddressPending = isHomeAddressDisplayed && isHomeAddressIncomplete;

  const isConfirmDisabled =
    hasNoBookablePatient ||
    isNoteTooLong ||
    isNoteMissing ||
    isHomeAddressPending;

  const containerStyle = cx({
    [styles['container']]: true,
    [styles['container-iframe']]: isLoadedInsideIframe,
  });

  const contentContainerStyle = cx({
    [styles['content-container']]: true,
    [styles['content-container-iframe']]: isLoadedInsideIframe,
  });

  const pageTitleStyle = cx({
    [styles['page-title']]: true,
    [styles['page-title-iframe']]: isLoadedInsideIframe,
  });

  return (
    <Layout>
      <Dialog open fullScreen>
        <div className={containerStyle}>
          {!isLoadedInsideIframe && (
            <div className={styles['header']}>
              <Header hasBoxShadow>
                <NavigationBar />
              </Header>
            </div>
          )}

          <div className={contentContainerStyle}>
            {createdBookingIds ? (
              isInsuranceStepPending ? (
                <InsuranceConfirmation
                  bookingIds={createdBookingIds}
                  insuranceName={contactPatient.insurance?.name}
                  onDone={() => setIsInsuranceDone(true)}
                />
              ) : (
                <BookingConfirmation />
              )
            ) : (
              <>
                <h2 className={pageTitleStyle}>Review appointment</h2>

                <BookingSummary
                  avatar={clinicData.avatar}
                  title={clinicData.name}
                  clinicAddress={clinicData.address}
                  isMainTitleHidden
                  description={
                    isSchedulingCountInSummary
                      ? renderSchedulingFor(bookablePatients.length)
                      : undefined
                  }
                />

                <BookingPatients
                  description={
                    isSchedulingCountUnderPatients
                      ? renderSchedulingFor(bookablePatients.length)
                      : null
                  }
                  mode={mode}
                  bookingPatients={bookingPatients}
                />

                <AdditionalInformation
                  isHomeAddressDisplayed={isHomeAddressDisplayed}
                  isNoteRequired={isNoteRequired}
                />

                <NavigationController
                  isDocked
                  isDisabledSubmit={isConfirmDisabled}
                  title={'Confirm'}
                  onClickBack={handleOnBackClick}
                  onClickNext={handleOnSubmit}
                  isLoading={isLoading}
                />
              </>
            )}
          </div>
        </div>
      </Dialog>
    </Layout>
  );
};

export default ReviewAppointment;
